import React, { useState, useEffect, useRef } from 'react';

import { AutocompleteProps, Autocomplete, AutocompleteItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteTaxRate from 'hooks/taxRate/useAutoCompleteTaxRate';

import SelectorButton from './SelectorButton';

export interface TaxRateAutoCompleteValue extends AutocompleteItem {
  taxRateId: string;
  label: string;
}

interface ITaxRateAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: TaxRateAutoCompleteValue;
  onChange: (item: TaxRateAutoCompleteValue | null) => void;
}

const TaxRateAutoComplete = ({
  value,
  onChange,
  ...autocompleteProps
}: ITaxRateAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteTaxRate({
    search: debouncedSearch,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: TaxRateAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.results.map(taxRate => ({
          ...taxRate,
          value: taxRate.taxRateId,
          label: taxRate.name,
        })),
      );
    }
  }, [isSuccess, data]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      {...autocompleteProps}
    />
  );
};

export default TaxRateAutoComplete;
